import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Layers, LockKeyhole } from "lucide-react";

export default function SignIn() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/app");
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="dot-bg flex flex-col justify-center px-6 py-16 lg:px-16">
        <div className="mx-auto w-full max-w-sm">
          <Logo size="text-3xl" />
          <h1 className="mt-10 font-head text-4xl font-black uppercase leading-none tracking-tight text-gray-100">
            Sign in to<br />the field.
          </h1>
          <p className="mt-4 text-sm text-gray-400">
            Access your projects, blueprints and live site position.
          </p>

          <button onClick={handleLogin} data-testid="google-signin-btn"
            className="mt-10 flex w-full items-center justify-center gap-3 border border-edge bg-white py-4 font-head text-base font-bold uppercase tracking-wide text-black transition-transform active:scale-[0.98]">
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </button>

          <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-widest text-gray-600">
            Free to start · No credit card
          </p>
        </div>
      </div>

      <div className="relative hidden bg-cardbg lg:block">
        <img src="https://images.unsplash.com/photo-1517089258673-4b72e709a9c5?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Aerial construction site" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-void via-void/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <h2 className="font-head text-3xl font-black uppercase leading-none tracking-tight text-gray-100">
            See the plan.<br /><span className="text-yellow">Know where you are.</span>
          </h2>
          <div className="mt-8 flex gap-6">
            {[[Layers, "Overlay"], [LockKeyhole, "Lock"], [MapPin, "Live GPS"]].map(([Icon, l], i) => (
              <div key={i} className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-yellow" />
                <span className="font-mono text-xs uppercase tracking-widest text-gray-300">{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
